import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { rpc, useRpcEvent } from '../../api/rpc'
import { IconRefresh } from '../icons'
import { Button } from '../ui/Button'
import { useToast } from '../ui/Toast'
import { RepoPicker } from './RepoPicker'
import type { RepoRef } from './useRepos'

interface Status {
  cloned: boolean
  branch?: string
  lastCommit?: string
  ahead?: number
  behind?: number
  checkedAt: number
}

/**
 * A slim shelf integrated directly above the composer input (docs/web-ui.md):
 * which repository this chat works on, branch/sync status, and clone/pull actions.
 *
 * It sits directly atop the composer card so it stays in context on both desktop
 * and mobile without taking up vertical chat space or hiding behind responsive menus.
 *
 * The status is fetched on demand, never on a timer: each call is a `docker exec` and may start a
 * stopped container. It reloads when the binding changes, so the dock never shows the previous
 * repository's branch.
 */
export function RepoDock({ repos, repoId, onBind, disabled }: {
  repos: RepoRef[]
  repoId: string | null
  onBind(repoId: string | null): void | Promise<void>
  disabled?: boolean
}) {
  const { t } = useTranslation()
  const toast = useToast()
  const [status, setStatus] = useState<Status | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    if (!repoId) { setStatus(null); setError(null); return }
    try {
      setStatus(await rpc.call<Status>('repos.status', { repoId }))
      setError(null)
    } catch (err) {
      // Keep the server's message: "the sandbox is not enabled" is actionable, a generic
      // "could not read the status" is not.
      setStatus(null)
      setError((err as Error).message)
    }
  }, [repoId])

  useEffect(() => { void load() }, [load])

  // The agent switches the branch, and the branch belongs to the shared clone: a chat that is not
  // this one may have moved it, so every open dock follows (docs/connectors.md §10.3).
  useRpcEvent('repo.updated', (p: { repoId: string }) => { if (p.repoId === repoId) void load() })

  const run = async (method: 'repos.clone' | 'repos.pull') => {
    setBusy(true)
    try {
      await rpc.call(method, { repoId })
      await load()
    } catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
  }

  // Nothing registered in this workspace: a picker with one option would be a dead control, so
  // point at where repositories are registered instead.
  if (repos.length === 0) {
    return (
      <div className="flex items-center gap-2 border-b border-line bg-cream-100 px-3 py-1.5 text-xs text-ink-500 rounded-t-2xl">
        <span>{t('repos.dockEmpty')}</span>
        <Link className="text-ginger-600 hover:underline" to="/settings/connectors">
          {t('repos.dockEmptyLink')}
        </Link>
      </div>
    )
  }

  // An unreadable status means the clone state is unknown, and Clone is the action that fixes the
  // commonest cause (nothing cloned yet). Hiding it would leave the user with no way forward.
  const unknown = status === null

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b border-line bg-cream-100 px-3 py-1 text-xs text-ink-500 rounded-t-2xl">
      <div className="flex min-w-0 items-center gap-2">
        <div className="min-w-0 max-w-[14rem] sm:max-w-[20rem]">
          <RepoPicker repos={repos} value={repoId} onChange={onBind} disabled={disabled} />
        </div>
        {repoId && (
          <>
            {unknown ? (
              <span className="truncate text-ink-400" title={error ?? undefined}>{error ?? t('repos.statusFailed')}</span>
            ) : status.cloned ? (
              <span className="flex min-w-0 items-center gap-1.5">
                {status.branch && (
                  <span
                    className="font-mono text-[11px] font-medium text-ink-400 truncate max-w-[120px] sm:max-w-[180px]"
                    title={t('repos.branchShared')}
                  >
                    {status.branch}
                  </span>
                )}
                {Boolean(status.ahead) && (
                  <span className="font-mono text-[10px] text-ginger-600 bg-ginger-50 border border-ginger-300 px-1 py-0.2 rounded font-medium shrink-0" title={t('repos.statusAhead', { count: status.ahead })}>
                    ↑{status.ahead}
                  </span>
                )}
                {Boolean(status.behind) && (
                  <span className="font-mono text-[10px] text-ink-500 bg-cream-200 border border-cream-300 px-1 py-0.2 rounded font-medium shrink-0" title={t('repos.statusBehind', { count: status.behind })}>
                    ↓{status.behind}
                  </span>
                )}
              </span>
            ) : (
              <span className="text-ink-400">{t('repos.statusNotCloned')}</span>
            )}
          </>
        )}
      </div>
      {repoId && (
        <div className="flex shrink-0 items-center gap-1 ml-auto">
          {unknown || !status.cloned ? (
            <Button size="sm" variant="secondary" loading={busy} disabled={disabled} onClick={() => void run('repos.clone')} className="h-6 px-2 text-[11px]">
              {t('repos.cloneNow')}
            </Button>
          ) : (
            <Button size="sm" variant="ghost" loading={busy} disabled={disabled} onClick={() => void run('repos.pull')} className="h-6 px-2 text-[11px] text-ink-400 hover:text-ink-100">
              {t('repos.pullNow')}
            </Button>
          )}
          <Button
            size="icon-xs"
            variant="ghost"
            disabled={disabled}
            onClick={() => void load()}
            aria-label={t('repos.refreshStatus')}
            title={t('repos.refreshStatus')}
            className="text-ink-400 hover:text-ink-100 shrink-0"
          >
            <IconRefresh width={13} height={13} />
          </Button>
        </div>
      )}
    </div>
  )
}
